import { type ReactNode } from 'react';

/**
 * Column descriptors, and the rules for which ones are shown.
 *
 * Pure, and tested exhaustively, because the interesting cases are all edge
 * cases: a stored preference naming a column that has since been removed, an
 * empty selection, an attempt to hide the column carrying the row link.
 * Each of those renders a broken table rather than throwing, which is the
 * kind of failure that reaches production.
 */

export interface ColumnDescriptor<TRow> {
  readonly id: string;
  readonly header: string;
  readonly cell: (row: TRow) => ReactNode;
  readonly sortable?: boolean;
  readonly align?: 'left' | 'right';

  /**
   * Accessible name for a header whose visible content is empty or an icon.
   *
   * An actions column with a blank header is announced as nothing during a
   * screen reader's table navigation, which loses the meaning of every cell
   * under it. Passed straight through to `DataTable`.
   */
  readonly headerLabel?: string;

  /**
   * Whether the column is on before the reader chooses. Defaults to `true`;
   * set `false` for a column that starts hidden.
   */
  readonly defaultVisible?: boolean;

  /**
   * Cannot be hidden — the identifying column and its row link.
   *
   * Takes precedence over everything. `alwaysVisible` with
   * `defaultVisible: false` is a contradiction, and the picker renders it as
   * a disabled checked entry rather than omitting it, so a reader can see why
   * one column has no toggle.
   */
  readonly alwaysVisible?: boolean;

  /**
   * The backend fields this column reads.
   *
   * A set, not one field: `Location` needs `building` and `floor`, `Rooms`
   * needs `totalRooms` and `sellableRooms`. Assuming one field per column
   * breaks on the first composite cell.
   *
   * Sent as `?fields=` and honoured by the tenants route, which rejects a name
   * it cannot project with a 422 rather than returning a blank column.
   * `tests/list-descriptors.test.tsx` checks every descriptor against that
   * route's allowlist, which is what makes the strict server safe.
   */
  readonly fields: readonly string[];
}

/** Columns shown when the reader has expressed no preference. */
function defaultColumns<TRow>(
  columns: readonly ColumnDescriptor<TRow>[],
): readonly ColumnDescriptor<TRow>[] {
  return columns.filter(
    (column) => column.alwaysVisible === true || column.defaultVisible !== false,
  );
}

/**
 * The columns to render.
 *
 * Order always comes from the descriptor list, never from the selection.
 * Otherwise the order in which someone happened to tick the boxes reorders
 * their table, and two people comparing the same screen see different layouts.
 */
export function resolveVisibleColumns<TRow>(
  columns: readonly ColumnDescriptor<TRow>[],
  selection: readonly string[] | undefined,
): readonly ColumnDescriptor<TRow>[] {
  // An empty selection means a stale or corrupted preference rather than a
  // deliberate request for an empty table, so the defaults are safer.
  if (selection === undefined || selection.length === 0) return defaultColumns(columns);

  const selected = new Set(selection);
  return columns.filter((column) => column.alwaysVisible === true || selected.has(column.id));
}

/**
 * The backend fields a set of columns needs.
 *
 * Sorted and de-duplicated so the value is canonical: it goes into the cache
 * key, and an unsorted list would make one request into several cache
 * entries. `id` is unconditional — `rowKey` and every row link need it
 * whatever the reader selected.
 */
export function fieldsForColumns<TRow>(
  columns: readonly ColumnDescriptor<TRow>[],
): readonly string[] {
  const fields = new Set<string>(['id']);
  for (const column of columns) {
    for (const field of column.fields) fields.add(field);
  }
  return [...fields].sort();
}

/**
 * Flips one column, returning the new selection.
 *
 * Two refusals, both to avoid producing a table nobody can use: an
 * `alwaysVisible` column cannot be removed, and neither can the last
 * removable one.
 */
export function toggleColumn<TRow>(
  columns: readonly ColumnDescriptor<TRow>[],
  selection: readonly string[] | undefined,
  columnId: string,
): readonly string[] {
  const descriptor = columns.find((column) => column.id === columnId);
  if (descriptor === undefined) return selection ?? defaultColumns(columns).map((c) => c.id);
  if (descriptor.alwaysVisible === true) {
    return selection ?? defaultColumns(columns).map((c) => c.id);
  }

  // Seeded from the defaults, so the first tick adds to what is on screen
  // rather than collapsing the table to a single column.
  const current = selection ?? defaultColumns(columns).map((column) => column.id);

  if (!current.includes(columnId)) return [...current, columnId];

  const remaining = current.filter((id) => id !== columnId);
  const removable = remaining.filter(
    (id) => columns.find((column) => column.id === id)?.alwaysVisible !== true,
  );

  return removable.length === 0 ? current : remaining;
}
